import React, { useEffect, useState, useMemo } from "react";
import {
  Container,
  Card,
  Row,
  Col,
  Button,
  Alert,
  Spinner,
  Badge,
  Form,
} from "react-bootstrap";
import { useAuth } from "../../contexts/AuthContext";
import { fires, auth } from "../../firebase";
import firebase from "firebase";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaEnvelope,
  FaCalendar,
  FaUsers,
  FaArrowLeft,
  FaPaperPlane,
  FaDollarSign,
  FaEye,
} from "react-icons/fa";

export default function AdminWeeklyEmailCampaignView() {
  const { currentUser } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [campaign, setCampaign] = useState(null);
  const [chunks, setChunks] = useState([]);
  const [loadingChunks, setLoadingChunks] = useState(false);
  const [recipientCount, setRecipientCount] = useState(0);
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [activityByEmail, setActivityByEmail] = useState({});
  const [analytics, setAnalytics] = useState({
    totalOpens: 0,
    uniqueOpens: 0,
    delivered: 0,
    sent: 0,
    openRate: 0,
    deliveredRate: 0,
    unsubscribed: 0,
    unsubscriptionRate: 0,
    bestOpenTime: null,
    hourDistribution: Array(24).fill(0), // Track opens by hour (0-23)
    timePeriodDistribution: { morning: 0, afternoon: 0, evening: 0, night: 0 }
  });
  const [unsubscribedUsers, setUnsubscribedUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [pageViews, setPageViews] = useState([]);
  const [loadingPageViews, setLoadingPageViews] = useState(false);
  const [campaignStats, setCampaignStats] = useState(null);
  const [campaignStatsLoading, setCampaignStatsLoading] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [emailForm, setEmailForm] = useState({
    subject: "",
    content: "",
  });

  useEffect(() => {
    checkAdminAccess();
  }, [currentUser]);

  useEffect(() => {
    if (isAdmin && id) {
      loadChunks();
      loadRecipients();
      loadActivities();
      loadOrders();
      loadPageViews();
    }
  }, [isAdmin, id]);

  // Load aggregated campaign stats from external service
  useEffect(() => {
    const loadCampaignStats = async () => {
      if (!isAdmin || !currentUser?.uid || !id) return;
      try {
        setCampaignStatsLoading(true);
        const params = new URLSearchParams({
          admin_uid: currentUser.uid,
          campaign_id: id,
        });
        const res = await fetch(
          `https://v2serviceapiloademailcampaigndata-650318705789.us-central1.run.app/?${params.toString()}`
        );
        const data = await res.json();
        if (res.ok && data?.success && data?.campaign) {
          setCampaignStats(data.campaign);
        } else {
          console.warn("Failed to load campaign stats", data);
        }
      } catch (err) {
        console.error("Error loading campaign stats:", err);
      } finally {
        setCampaignStatsLoading(false);
      }
    };

    loadCampaignStats();
  }, [isAdmin, currentUser, id]);

  const campaignStatsData = campaignStats || (campaign && campaign.campaign) || null;

  // Recalculate analytics when activities or chunks change
  useEffect(() => {
    if (activities.length > 0 || chunks.length > 0) {
      calculateAnalytics();
    }
  }, [activities, chunks]);

  useEffect(() => {
    const firstChunkWithEmail = chunks.find((chunk) => chunk.subject || chunk.content);
    setEmailForm({
      subject: campaign?.subject || campaign?.title || firstChunkWithEmail?.subject || "",
      content: campaign?.content || firstChunkWithEmail?.content || "",
    });
  }, [campaign, chunks]);

  const checkAdminAccess = async () => {
    try {
      if (!currentUser) {
        setError("Please log in to access this page");
        setLoading(false);
        return;
      }

      const adminDoc = await fires.collection("admin").doc(currentUser.uid).get();
      if (!adminDoc.exists) {
        setError("Access denied. Admin privileges required.");
        setLoading(false);
        return;
      }

      const adminData = adminDoc.data();
      if (!adminData.roles || !adminData.roles.includes("management")) {
        setError("Access denied. Management role required.");
        setLoading(false);
        return;
      }

      setIsAdmin(true);
      setLoading(false);
    } catch (err) {
      console.error("Error checking admin access:", err);
      setError("Error verifying admin access");
      setLoading(false);
    }
  };

  const loadChunks = async () => {
    try {
      setLoadingChunks(true);
      setError("");

      // Load all chunks from the chunks subcollection
      const chunksSnapshot = await fires
        .collection("campaigns")
        .doc(id)
        .collection("chunks")
        .get();

      if (chunksSnapshot.empty) {
        setError("No chunks found for this campaign");
        setLoadingChunks(false);
        return;
      }

      const chunksData = [];
      chunksSnapshot.forEach((doc) => {
        chunksData.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      setChunks(chunksData);
      
      // Also load the campaign document for reference
      const campaignDoc = await fires.collection("campaigns").doc(id).get();
      if (campaignDoc.exists) {
        const campaignData = campaignDoc.data();
        setCampaign({
          id: campaignDoc.id,
          ...campaignData,
        });
      }
    } catch (err) {
      console.error("Error loading chunks:", err);
      setError("Error loading chunks: " + err.message);
    } finally {
      setLoadingChunks(false);
    }
  };

  const loadRecipients = async () => {
    try {
      const usersSnapshot = await fires.collection("users").get();
      const recipients = [];
      usersSnapshot.forEach((doc) => {
        const userData = doc.data();
        if (userData.email) {
          recipients.push(userData.email);
        }
      });
      setRecipientCount(recipients.length);
      return recipients;
    } catch (err) {
      console.error("Error loading recipients:", err);
      return [];
    }
  };

  const loadOrders = async () => {
    try {
      setLoadingOrders(true);
      
      // Query orders filtered by campaignId
      const ordersSnapshot = await fires
        .collection("orders")
        .where("viewData.metadata.campaignId", "==", id)
        .get();

      const ordersData = [];
      ordersSnapshot.forEach((doc) => {
        const orderData = doc.data();
        ordersData.push({
          id: doc.id,
          amount: orderData.amount || orderData.totalAmount || orderData.total || 0,
          userEmail: orderData.userEmail || orderData.email || orderData.user?.email || "unknown",
          title: orderData.title || orderData.eventTitle || orderData.viewData?.title || orderData.viewData?.eventTitle || "N/A",
          ...orderData
        });
      });

      setOrders(ordersData);
    } catch (err) {
      console.error("Error loading orders:", err);
      setError("Error loading orders: " + err.message);
    } finally {
      setLoadingOrders(false);
    }
  };

  const loadPageViews = async () => {
    try {
      setLoadingPageViews(true);
      
      // Query page views filtered by campaignId
      const pageViewsSnapshot = await fires
        .collection("page_views")
        .where("metadata.campaignId", "==", id)
        .get();

      const pageViewsData = [];
      pageViewsSnapshot.forEach((doc) => {
        pageViewsData.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      setPageViews(pageViewsData);
    } catch (err) {
      console.error("Error loading page views:", err);
      setError("Error loading page views: " + err.message);
    } finally {
      setLoadingPageViews(false);
    }
  };

  const getHour = (timestamp) => {
    if (!timestamp) return null;
    
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (timestamp.toMillis) {
      date = new Date(timestamp.toMillis());
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else if (typeof timestamp === 'number') {
      date = new Date(timestamp);
    } else if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    } else {
      return null;
    }
    
    return date.getHours(); // Returns 0-23
  };

  const loadActivities = async () => {
    try {
      setLoadingActivities(true);
      
      // Load all activities from the activity subcollection
      const activitiesSnapshot = await fires
        .collection("campaigns")
        .doc(id)
        .collection("activity")
        .get();

      const activitiesData = [];
      activitiesSnapshot.forEach((doc) => {
        activitiesData.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      setActivities(activitiesData);

      // Group activities by email
      const grouped = {};
      activitiesData.forEach((activity) => {
        const email = activity.email || "unknown";
        if (!grouped[email]) {
          grouped[email] = {
            email: email,
            statuses: {},
            total: 0,
          };
        }
        
        const status = activity.status || "unknown";
        
        // Normalize status values
        let normalizedStatus = status;
        if (status === "activity.opened") {
          normalizedStatus = "opened";
        } else if (status === "activity.delivered") {
          normalizedStatus = "delivered";
        } else if (status === "activity.sent") {
          normalizedStatus = "sent";
        } else if (status === "activity.clicked") {
          normalizedStatus = "clicked";
        } else if (status === "activity.bounced") {
          normalizedStatus = "bounced";
        } else if (status === "unsubscribed" || status === "activity.unsubscribed") {
          normalizedStatus = "unsubscribed";
        }
        
        if (!grouped[email].statuses[normalizedStatus]) {
          grouped[email].statuses[normalizedStatus] = 0;
        }
        grouped[email].statuses[normalizedStatus]++;
        grouped[email].total++;
      });

      setActivityByEmail(grouped);

      // Filter unsubscribed users
      const unsubscribed = activitiesData
        .filter(activity => {
          const status = activity.status || "unknown";
          return status === "unsubscribed" || status === "activity.unsubscribed";
        })
        .map(activity => ({
          email: activity.email || "unknown",
          timestamp: activity.timestamp || activity.createdAt || activity.date,
          id: activity.id
        }))
        .filter(user => user.email !== "unknown");

      // Remove duplicates by email (keep first occurrence)
      const uniqueUnsubscribed = [];
      const seenEmails = new Set();
      unsubscribed.forEach(user => {
        if (!seenEmails.has(user.email.toLowerCase())) {
          seenEmails.add(user.email.toLowerCase());
          uniqueUnsubscribed.push(user);
        }
      });

      setUnsubscribedUsers(uniqueUnsubscribed);
    } catch (err) {
      console.error("Error loading activities:", err);
    } finally {
      setLoadingActivities(false);
    }
  };

  const calculateAnalytics = () => {
    if (activities.length === 0 && chunks.length === 0) return;

    // Calculate analytics
    let totalOpens = 0;
    const uniqueOpenEmails = new Set();
    let delivered = 0;
    let sent = 0;
    let unsubscribed = 0;
    const hourDistribution = Array(24).fill(0); // Track opens by hour (0-23)

    activities.forEach((activity) => {
      const status = activity.status || "unknown";
      
      // Check if status is "activity.opened" (string value)
      if (status === "activity.opened") {
        totalOpens++;
        if (activity.email) {
          uniqueOpenEmails.add(activity.email);
        }
        
        // Track hour for opens (0-23)
        const hour = getHour(activity.timestamp || activity.createdAt || activity.date);
        if (hour !== null && hour >= 0 && hour < 24) {
          hourDistribution[hour]++;
        }
      }
      
      // Check if status is "activity.delivered" (string value)
      if (status === "activity.delivered") {
        delivered++;
      }
      
      // Check if status is "activity.sent" (string value)
      if (status === "activity.sent") {
        sent++;
      }
      
      // Check if status is "unsubscribed" or "activity.unsubscribed"
      if (status === "unsubscribed" || status === "activity.unsubscribed") {
        unsubscribed++;
      }
    });

    // If no "sent" status in activities, count from chunks
    if (sent === 0 && chunks.length > 0) {
      chunks.forEach((chunk) => {
        if (chunk.status === "sent" || chunk.status === "completed") {
          const emailCount = Array.isArray(chunk.emails) 
            ? chunk.emails.length 
            : chunk.emailCount || chunk.email_count || chunk.recipientCount || chunk.recipient_count || 0;
          sent += emailCount;
        }
      });
    }

    const uniqueOpens = uniqueOpenEmails.size;
    const openRate = delivered > 0 ? (uniqueOpens / delivered) * 100 : 0;
    const deliveredRate = sent > 0 ? (delivered / sent) * 100 : 0;
    
    // Count unique unsubscribed emails (not total unsubscribed activities)
    const uniqueUnsubscribedEmails = new Set();
    activities.forEach((activity) => {
      const status = activity.status || "unknown";
      if ((status === "unsubscribed" || status === "activity.unsubscribed") && activity.email) {
        uniqueUnsubscribedEmails.add(activity.email.toLowerCase());
      }
    });
    const uniqueUnsubscribedCount = uniqueUnsubscribedEmails.size;
    const unsubscriptionRate = sent > 0 ? (uniqueUnsubscribedCount / sent) * 100 : 0;

    // Find best hour (hour with most opens)
    const maxOpens = Math.max(...hourDistribution);
    const bestHour = maxOpens > 0 ? hourDistribution.indexOf(maxOpens) : null;
    const bestOpenTime = bestHour !== null 
      ? `${bestHour.toString().padStart(2, '0')}:00 - ${(bestHour + 1).toString().padStart(2, '0')}:00` 
      : null;

    // Calculate time period totals
    // Morning: 6am-12pm (hours 6-11)
    // Afternoon: 12pm-6pm (hours 12-17)
    // Evening: 6pm-10pm (hours 18-21)
    // Night: 10pm-6am (hours 22-23 and 0-5)
    const timePeriodDistribution = {
      morning: hourDistribution.slice(6, 12).reduce((sum, count) => sum + count, 0),
      afternoon: hourDistribution.slice(12, 18).reduce((sum, count) => sum + count, 0),
      evening: hourDistribution.slice(18, 22).reduce((sum, count) => sum + count, 0),
      night: hourDistribution.slice(22, 24).reduce((sum, count) => sum + count, 0) + 
             hourDistribution.slice(0, 6).reduce((sum, count) => sum + count, 0)
    };

    setAnalytics({
      totalOpens,
      uniqueOpens,
      delivered,
      sent,
      openRate: parseFloat(openRate.toFixed(2)),
      deliveredRate: parseFloat(deliveredRate.toFixed(2)),
      unsubscribed: uniqueUnsubscribedCount,
      unsubscriptionRate: parseFloat(unsubscriptionRate.toFixed(2)),
      bestOpenTime,
      hourDistribution,
      timePeriodDistribution
    });
  };

  const sendCampaign = async () => {
    // Filter chunks with status that is NOT "sent"
    const chunksToSend = chunks.filter(chunk => chunk.status !== "sent");
    
    if (chunksToSend.length === 0) {
      setError("No chunks available to send (all chunks have been sent)");
      return;
    }

    try {
      setSending(true);
      setError("");
      setSuccess("");

      const token = await auth.currentUser.getIdToken();
      
      // Send each chunk sequentially
      let successCount = 0;
      let failedCount = 0;
      
      for (let i = 0; i < chunksToSend.length; i++) {
        const chunk = chunksToSend[i];
        const isLastChunk = i === chunksToSend.length - 1;
        
        try {
          const apiUrl = `https://v2admincreateandsendemailscampaign-650318705789.us-central1.run.app/`;

          const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              mode: 'send',
              chunk_id: chunk.id,
              campaign_id: id
            })
          });
          
          const data = await response.json();
          
          if (data.success || data.status === "success") {
            successCount++;
            
            // If this is the last chunk, mark campaign as fully sent
            if (isLastChunk) {
              // Last chunk sent - campaign is fully sent
              console.log("Campaign fully sent - all chunks have been sent");
            }
          } else {
            failedCount++;
          }
        } catch (chunkErr) {
          console.error(`Error sending chunk ${chunk.id}:`, chunkErr);
          failedCount++;
        }
      }
      
      // Reload chunks and activities to get updated status
      await loadChunks();
      await loadActivities();
      
      if (failedCount === 0) {
        setSuccess(`Campaign sent successfully! Sent ${successCount} chunk(s).`);
      } else if (successCount > 0) {
        setError(`Partially sent: ${successCount} succeeded, ${failedCount} failed.`);
      } else {
        setError("Failed to send all chunks. Please try again.");
      }
    } catch (err) {
      console.error("Error sending campaign:", err);
      setError("Error sending campaign: " + err.message);
    } finally {
      setSending(false);
    }
  };

  const saveCampaignEmail = async () => {
    const subject = emailForm.subject.trim();
    const content = emailForm.content.trim();

    if (!subject) {
      setError("Please enter an email subject.");
      return;
    }

    if (!content) {
      setError("Please enter email content.");
      return;
    }

    try {
      setSavingEmail(true);
      setError("");
      setSuccess("");

      const batch = fires.batch();
      const updatedAt = firebase.firestore.FieldValue.serverTimestamp();
      const campaignRef = fires.collection("campaigns").doc(id);
      const emailUpdate = {
        subject,
        title: subject,
        content,
        updatedAt,
        updatedBy: currentUser?.uid || null,
      };

      batch.update(campaignRef, emailUpdate);
      chunks.forEach((chunk) => {
        const chunkRef = campaignRef.collection("chunks").doc(chunk.id);
        batch.update(chunkRef, {
          subject,
          content,
          updatedAt,
          updatedBy: currentUser?.uid || null,
        });
      });

      await batch.commit();
      await loadChunks();
      setSuccess("Campaign email updated successfully.");
    } catch (err) {
      console.error("Error updating campaign email:", err);
      setError("Error updating campaign email: " + err.message);
    } finally {
      setSavingEmail(false);
    }
  };

  // Calculate available chunks to send (chunks that are not "sent")
  const availableChunksCount = chunks.filter(chunk => chunk.status !== "sent").length;

  // Group page views by eventId and rank by count
  const pageViewsByEventId = useMemo(() => {
    const grouped = {};
    let total = 0;
    
    pageViews.forEach((pageView) => {
      const eventId = pageView.eventId || pageView.metadata?.eventId || "unknown";
      if (!grouped[eventId]) {
        grouped[eventId] = 0;
      }
      grouped[eventId]++;
      total++;
    });
    
    // Convert to array and sort by count (descending)
    const sorted = Object.entries(grouped)
      .map(([eventId, count]) => ({ eventId, count }))
      .sort((a, b) => b.count - a.count);
    
    return { grouped, total, sorted };
  }, [pageViews]);

  // Rank users by order value, order count, and opens
  const rankedUsers = useMemo(() => {
    // Combine orders and activities by email
    const userMetrics = {};
    
    // Process orders
    orders.forEach((order) => {
      const email = order.userEmail?.toLowerCase() || "unknown";
      if (!userMetrics[email]) {
        userMetrics[email] = {
          email: order.userEmail || "unknown",
          orderValue: 0,
          orderCount: 0,
          opens: 0,
          activities: {}
        };
      }
      userMetrics[email].orderValue += parseFloat(order.amount || 0);
      userMetrics[email].orderCount += 1;
    });
    
    // Process activities
    Object.entries(activityByEmail).forEach(([email, emailData]) => {
      const emailLower = email.toLowerCase();
      if (!userMetrics[emailLower]) {
        userMetrics[emailLower] = {
          email: email,
          orderValue: 0,
          orderCount: 0,
          opens: 0,
          activities: {}
        };
      }
      userMetrics[emailLower].opens = emailData.statuses["opened"] || 0;
      userMetrics[emailLower].activities = emailData.statuses;
    });
    
    // Convert to array and sort by: order value (desc), order count (desc), opens (desc)
    return Object.values(userMetrics).sort((a, b) => {
      // First by order value
      if (b.orderValue !== a.orderValue) {
        return b.orderValue - a.orderValue;
      }
      // Then by order count
      if (b.orderCount !== a.orderCount) {
        return b.orderCount - a.orderCount;
      }
      // Finally by opens
      return b.opens - a.opens;
    });
  }, [orders, activityByEmail]);

  if (loading) {
    return (
      <Container className="py-5">
        <div className="d-flex justify-content-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (!isAdmin) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <Alert.Heading>Access Denied</Alert.Heading>
          <p>{error || "You do not have permission to access this page."}</p>
        </Alert>
      </Container>
    );
  }

  if (loadingChunks) {
    return (
      <Container className="py-5">
        <div className="d-flex justify-content-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading chunks...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (chunks.length === 0 && !loading) {
    return (
      <Container className="py-5">
        <Alert variant="warning">
          <Alert.Heading>No Chunks Found</Alert.Heading>
          <p>{error || "No chunks found for this campaign."}</p>
          <Button variant="primary" onClick={() => navigate("/admin-weekly-email-campaign")}>
            <FaArrowLeft className="me-2" />
            Back to Campaigns
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <Button
                variant="outline-secondary"
                onClick={() => navigate("/admin-weekly-email-campaign")}
                className="mb-3"
              >
                <FaArrowLeft className="me-2" />
                Back to Campaigns
              </Button>
              <h2 className="mb-1">
                <FaEnvelope className="me-2" />
                Campaign Chunks
              </h2>
              <p className="text-muted mb-0">
                View and manage campaign chunks (Campaign ID: {id})
              </p>
            </div>
            <div>
              <Button
                variant="primary"
                onClick={sendCampaign}
                disabled={sending || chunks.length === 0 || availableChunksCount === 0}
                className="d-flex align-items-center"
              >
                {sending ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Sending...
                  </>
                ) : (
                  <>
                    <FaPaperPlane className="me-2" />
                    Send Campaign {availableChunksCount > 0 && `(${availableChunksCount})`}
                  </>
                )}
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {/* Alerts */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Row>
        <Col lg={8}>
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-info text-white">
              <h5 className="mb-0">
                <FaEnvelope className="me-2" />
                Modify Campaign Email
              </h5>
            </Card.Header>
            <Card.Body>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Email Subject</Form.Label>
                  <Form.Control
                    type="text"
                    value={emailForm.subject}
                    onChange={(e) =>
                      setEmailForm((prev) => ({
                        ...prev,
                        subject: e.target.value,
                      }))
                    }
                    disabled={savingEmail || sending}
                    placeholder="Enter campaign email subject"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Email Content</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={12}
                    value={emailForm.content}
                    onChange={(e) =>
                      setEmailForm((prev) => ({
                        ...prev,
                        content: e.target.value,
                      }))
                    }
                    disabled={savingEmail || sending}
                    placeholder="Enter campaign email HTML/content"
                    style={{ fontFamily: "monospace", fontSize: "0.9rem" }}
                  />
                  <Form.Text className="text-muted">
                    You can use HTML. Changes are saved to the campaign and all campaign chunks.
                  </Form.Text>
                </Form.Group>

                <div className="d-flex justify-content-between align-items-center mb-3">
                  <Button
                    variant="primary"
                    onClick={saveCampaignEmail}
                    disabled={savingEmail || sending || !emailForm.subject.trim() || !emailForm.content.trim()}
                  >
                    {savingEmail ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Saving...
                      </>
                    ) : (
                      "Save Email"
                    )}
                  </Button>
                  {chunks.some((chunk) => chunk.status === "sent") && (
                    <small className="text-muted">
                      Some chunks are already sent; updates apply to stored campaign content.
                    </small>
                  )}
                </div>
              </Form>

              <details>
                <summary className="text-muted" style={{ cursor: "pointer" }}>
                  Preview email content
                </summary>
                <div
                  className="border rounded p-3 mt-2"
                  style={{ maxHeight: "400px", overflowY: "auto" }}
                  dangerouslySetInnerHTML={{
                    __html: emailForm.content || "No content",
                  }}
                />
              </details>
            </Card.Body>
          </Card>

          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-primary text-white">
              <h5 className="mb-0">
                <FaEnvelope className="me-2" />
                Chunks ({chunks.length})
              </h5>
            </Card.Header>
            <Card.Body>
              {loadingChunks ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                  <p className="text-muted mt-2 mb-0">Loading chunks...</p>
                </div>
              ) : chunks.length === 0 ? (
                <p className="text-muted text-center py-3">
                  No chunks found
                </p>
              ) : (
                <div style={{ maxHeight: "600px", overflowY: "auto" }}>
                  {chunks.map((chunk) => (
                    <div
                      key={chunk.id}
                      className="mb-3 p-3 border rounded"
                    >
                      <Row className="mb-2">
                        <Col md={4}>
                          <strong>Chunk ID:</strong>
                          <p className="text-muted mb-0">{chunk.id}</p>
                        </Col>
                        <Col md={4}>
                          <strong>
                            <FaUsers className="me-1" />
                            Emails:
                          </strong>
                          <p className="text-muted mb-0">
                            {Array.isArray(chunk.emails) 
                              ? chunk.emails.length 
                              : chunk.emailCount || chunk.email_count || chunk.recipientCount || chunk.recipient_count || 0}
                          </p>
                        </Col>
                        <Col md={4}>
                          <strong>Sent Status:</strong>
                          <div className="mt-1">
                            <Badge
                              bg={chunk.status === "sent" ? "success" : "secondary"}
                            >
                              {chunk.status === "sent" ? "Sent" : chunk.status || "Not Sent"}
                            </Badge>
                          </div>
                        </Col>
                      </Row>
                      
                      {chunk.status && chunk.status !== "sent" && (
                        <Row className="mb-2">
                          <Col>
                            <strong>Status:</strong>
                            <div className="mt-1">
                              <Badge
                                bg={
                                  chunk.status === "queued"
                                    ? "warning"
                                    : chunk.status === "completed"
                                    ? "info"
                                    : "danger"
                                }
                              >
                                {chunk.status}
                              </Badge>
                            </div>
                          </Col>
                        </Row>
                      )}

                      {chunk.subject && (
                        <Row className="mb-2">
                          <Col>
                            <strong>Subject:</strong>
                            <p className="text-muted mb-0">{chunk.subject}</p>
                          </Col>
                        </Row>
                      )}

                      {chunk.content && (
                        <Row className="mb-2">
                          <Col>
                            <strong>Content:</strong>
                            <div
                              className="border rounded p-2 mt-1"
                              style={{ maxHeight: "200px", overflowY: "auto" }}
                              dangerouslySetInnerHTML={{
                                __html: chunk.content || "No content",
                              }}
                            />
                          </Col>
                        </Row>
                      )}

                      {chunk.timestamp && (
                        <Row>
                          <Col>
                            <small className="text-muted">
                              <FaCalendar className="me-1" />
                              {chunk.timestamp?.toDate
                                ? chunk.timestamp.toDate().toLocaleString()
                                : chunk.timestamp?.toMillis
                                ? new Date(chunk.timestamp.toMillis()).toLocaleString()
                                : "N/A"}
                            </small>
                          </Col>
                        </Row>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-secondary text-white">
              <h5 className="mb-0">Actions</h5>
            </Card.Header>
            <Card.Body>
              <div className="d-grid gap-2">
                <Button
                  variant="primary"
                  onClick={sendCampaign}
                  disabled={sending || availableChunksCount === 0}
                  className="d-flex align-items-center justify-content-center"
                >
                  {sending ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <FaPaperPlane className="me-2" />
                      Send Campaign {availableChunksCount > 0 && `(${availableChunksCount})`}
                    </>
                  )}
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate("/admin-weekly-email-campaign")}
                  className="d-flex align-items-center justify-content-center"
                >
                  <FaArrowLeft className="me-2" />
                  Back to Campaigns
                </Button>
              </div>

              {campaign && (
                <div className="mt-3">
                  <strong>Campaign Info:</strong>
                  <p className="text-muted mb-0 small">
                    Campaign ID: {campaign.id || id}
                  </p>
                  {campaign.title && (
                    <p className="text-muted mb-0 small">
                      Title: {campaign.title}
                    </p>
                  )}

                  {/* Campaign performance metrics (from external stats API, with Firestore fallback) */}
                  {campaignStatsLoading && !campaignStatsData && (
                    <div className="mt-2 small text-muted">Loading campaign performance…</div>
                  )}
                  {campaignStatsData && (
                    <div className="mt-2">
                      <Row className="g-2">
                        <Col xs={6} md={3}>
                          <div className="small text-muted">Sent</div>
                          <div className="fw-semibold small">
                            {campaignStatsData.sentCount ?? 0}
                          </div>
                        </Col>
                        <Col xs={6} md={3}>
                          <div className="small text-muted">Delivered</div>
                          <div className="fw-semibold small">
                            {campaignStatsData.deliveryCount ?? 0}
                            {campaignStatsData.deliveryRate != null && (
                              <span className="text-muted ms-1">
                                ({campaignStatsData.deliveryRate.toFixed
                                  ? campaignStatsData.deliveryRate.toFixed(2)
                                  : campaignStatsData.deliveryRate}
                                %)
                              </span>
                            )}
                          </div>
                        </Col>
                        <Col xs={6} md={3}>
                          <div className="small text-muted">Unsubscribes</div>
                          <div className="fw-semibold small">
                            {campaignStatsData.unsubscribeCount ?? 0}
                            {campaignStatsData.unsubscribeRate != null && (
                              <span className="text-muted ms-1">
                                ({campaignStatsData.unsubscribeRate.toFixed
                                  ? campaignStatsData.unsubscribeRate.toFixed(2)
                                  : campaignStatsData.unsubscribeRate}
                                %)
                              </span>
                            )}
                          </div>
                        </Col>
                        <Col xs={6} md={3}>
                          <div className="small text-muted">Opens</div>
                          <div className="fw-semibold small">
                            {campaignStatsData.openCount ?? 0}{" "}
                            <span className="text-muted">
                              ({campaignStatsData.uniqueOpenCount ?? 0} unique)
                            </span>
                            {campaignStatsData.openRate != null && (
                              <span className="text-muted ms-1">
                                ({campaignStatsData.openRate.toFixed
                                  ? campaignStatsData.openRate.toFixed(2)
                                  : campaignStatsData.openRate}
                                %)
                              </span>
                            )}
                          </div>
                        </Col>
                        <Col xs={6} md={3}>
                          <div className="small text-muted">Page Views</div>
                          <div className="fw-semibold small">
                            {campaignStatsData.pageViewCount ?? 0}
                            {campaignStatsData.pageViewRate != null && (
                              <span className="text-muted ms-1">
                                ({campaignStatsData.pageViewRate.toFixed
                                  ? campaignStatsData.pageViewRate.toFixed(2)
                                  : campaignStatsData.pageViewRate}
                                %)
                              </span>
                            )}
                          </div>
                        </Col>
                        <Col xs={6} md={3}>
                          <div className="small text-muted">Orders</div>
                          <div className="fw-semibold small">
                            {campaignStatsData.orderCount ?? 0}
                            {campaignStatsData.orderRate != null && (
                              <span className="text-muted ms-1">
                                ({campaignStatsData.orderRate.toFixed
                                  ? campaignStatsData.orderRate.toFixed(2)
                                  : campaignStatsData.orderRate}
                                %)
                              </span>
                            )}
                          </div>
                        </Col>
                        <Col xs={6} md={3}>
                          <div className="small text-muted">Order Amount</div>
                          <div className="fw-semibold small">
                            ${Number(campaignStatsData.orderAmount ?? 0).toFixed(2)}
                          </div>
                        </Col>
                      </Row>
                    </div>
                  )}
                </div>
              )}

              {/* Orders Section */}
              <div className="mt-4 pt-3 border-top">
                <h6 className="mb-3">
                  <FaDollarSign className="me-2" />
                  Campaign Orders
                </h6>
                {loadingOrders ? (
                  <div className="text-center py-2">
                    <Spinner animation="border" size="sm" />
                    <p className="text-muted mt-2 mb-0 small">Loading orders...</p>
                  </div>
                ) : orders.length === 0 ? (
                  <p className="text-muted small mb-0">No orders found for this campaign</p>
                ) : (
                  <div>
                    <div className="mb-2">
                      <Badge bg="success" className="mb-2">
                        Total Orders: {orders.length}
                      </Badge>
                      <Badge bg="primary" className="ms-2 mb-2">
                        Total Amount: ${orders.reduce((sum, order) => sum + (parseFloat(order.amount) || 0), 0).toFixed(2)}
                      </Badge>
                    </div>
                    <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                      {orders.map((order) => (
                        <div key={order.id} className="mb-2 p-2 border rounded">
                          <div className="mb-1">
                            <strong className="small">Event:</strong>
                            <div className="small text-muted mb-1">{order.title}</div>
                          </div>
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <div className="small">
                              <strong>Email:</strong> {order.userEmail}
                            </div>
                            <Badge bg="success">
                              ${parseFloat(order.amount || 0).toFixed(2)}
                            </Badge>
                          </div>
                          <small className="text-muted">Order ID: {order.id}</small>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Page Views Section */}
              <div className="mt-4 pt-3 border-top">
                <h6 className="mb-3">
                  <FaEye className="me-2" />
                  Page Views
                </h6>
                {loadingPageViews ? (
                  <div className="text-center py-2">
                    <Spinner animation="border" size="sm" />
                    <p className="text-muted mt-2 mb-0 small">Loading page views...</p>
                  </div>
                ) : pageViews.length === 0 ? (
                  <p className="text-muted small mb-0">No page views found for this campaign</p>
                ) : (
                  <div>
                    <div className="mb-3">
                      <Badge bg="info" className="mb-2">
                        Total Page Views: {pageViewsByEventId.total}
                      </Badge>
                    </div>
                    <div className="small">
                      {pageViewsByEventId.sorted.map((item, index) => (
                        <div key={item.eventId} className="d-flex justify-content-between mb-2 p-2 bg-light rounded">
                          <span>
                            <Badge bg="warning" className="me-2">#{index + 1}</Badge>
                            <strong>Event ID:</strong> {item.eventId}
                          </span>
                          <Badge bg="secondary">{item.count}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Analytics Section */}
              <div className="mt-4 pt-3 border-top">
                <h6 className="mb-3">
                  <FaUsers className="me-2" />
                  Campaign Analytics
                </h6>
                {loadingActivities ? (
                  <div className="text-center py-2">
                    <Spinner animation="border" size="sm" />
                    <p className="text-muted mt-2 mb-0 small">Loading analytics...</p>
                  </div>
                ) : activities.length === 0 ? (
                  <p className="text-muted small mb-0">No activity data available</p>
                ) : (
                  <div>
                    {/* Open Rate */}
                    <div className="mb-3 p-2 bg-light rounded">
                      <strong className="small d-block mb-2">Open Rate</strong>
                      <div className="small">
                        <div className="d-flex justify-content-between mb-1">
                          <span>Total Opens:</span>
                          <Badge bg="primary">{analytics.totalOpens}</Badge>
                        </div>
                        <div className="d-flex justify-content-between mb-1">
                          <span>Unique Opens:</span>
                          <Badge bg="info">{analytics.uniqueOpens}</Badge>
                        </div>
                        <div className="d-flex justify-content-between mb-1">
                          <span>Delivered:</span>
                          <Badge bg="success">{analytics.delivered}</Badge>
                        </div>
                        <div className="d-flex justify-content-between mt-2 pt-2 border-top">
                          <strong>Open Rate:</strong>
                          <Badge bg={analytics.openRate >= 20 ? "success" : analytics.openRate >= 10 ? "warning" : "danger"}>
                            {analytics.openRate}%
                          </Badge>
                        </div>
                        <small className="text-muted d-block mt-1">
                          ({analytics.uniqueOpens} unique opens / {analytics.delivered} delivered)
                        </small>
                      </div>
                    </div>

                    {/* Delivered Rate */}
                    <div className="mb-3 p-2 bg-light rounded">
                      <strong className="small d-block mb-2">Delivered Rate</strong>
                      <div className="small">
                        <div className="d-flex justify-content-between mb-1">
                          <span>Delivered:</span>
                          <Badge bg="info">{analytics.delivered}</Badge>
                        </div>
                        <div className="d-flex justify-content-between mb-1">
                          <span>Sent:</span>
                          <Badge bg="secondary">{analytics.sent}</Badge>
                        </div>
                        <div className="d-flex justify-content-between mt-2 pt-2 border-top">
                          <strong>Delivered Rate:</strong>
                          <Badge bg={analytics.deliveredRate >= 95 ? "success" : analytics.deliveredRate >= 90 ? "warning" : "danger"}>
                            {analytics.deliveredRate}%
                          </Badge>
                        </div>
                        <small className="text-muted d-block mt-1">
                          ({analytics.delivered} delivered / {analytics.sent} sent)
                        </small>
                      </div>
                    </div>

                    {/* Unsubscription Rate */}
                    <div className="mb-3 p-2 bg-light rounded">
                      <strong className="small d-block mb-2">Unsubscription Rate</strong>
                      <div className="small">
                        <div className="d-flex justify-content-between mb-1">
                          <span>Unsubscribed:</span>
                          <Badge bg="danger">{analytics.unsubscribed}</Badge>
                        </div>
                        <div className="d-flex justify-content-between mb-1">
                          <span>Total Sent:</span>
                          <Badge bg="secondary">{analytics.sent}</Badge>
                        </div>
                        <div className="d-flex justify-content-between mt-2 pt-2 border-top">
                          <strong>Unsubscription Rate:</strong>
                          <Badge bg={analytics.unsubscriptionRate <= 1 ? "success" : analytics.unsubscriptionRate <= 3 ? "warning" : "danger"}>
                            {analytics.unsubscriptionRate}%
                          </Badge>
                        </div>
                        <small className="text-muted d-block mt-1">
                          ({analytics.unsubscribed} unsubscribed / {analytics.sent} sent)
                        </small>
                      </div>
                    </div>

                    {/* Best Open Time */}
                    {analytics.bestOpenTime && (
                      <div className="mb-3 p-2 bg-light rounded">
                        <strong className="small d-block mb-2">Best Open Time</strong>
                        <div className="small">
                          <div className="mb-2">
                            <Badge bg="primary" className="mb-2">{analytics.bestOpenTime}</Badge>
                          </div>
                          
                          {/* Time Period Summary */}
                          {analytics.timePeriodDistribution && (
                            <div className="mb-3 pb-2 border-bottom">
                              <div className="d-flex justify-content-between mb-1">
                                <span>Morning (6am-12pm):</span>
                                <Badge bg="info">{analytics.timePeriodDistribution.morning}</Badge>
                              </div>
                              <div className="d-flex justify-content-between mb-1">
                                <span>Afternoon (12pm-6pm):</span>
                                <Badge bg="info">{analytics.timePeriodDistribution.afternoon}</Badge>
                              </div>
                              <div className="d-flex justify-content-between mb-1">
                                <span>Evening (6pm-10pm):</span>
                                <Badge bg="info">{analytics.timePeriodDistribution.evening}</Badge>
                              </div>
                              <div className="d-flex justify-content-between">
                                <span>Night (10pm-6am):</span>
                                <Badge bg="info">{analytics.timePeriodDistribution.night}</Badge>
                              </div>
                            </div>
                          )}

                          {/* 24-Hour Breakdown */}
                          <div className="mt-2" style={{ maxHeight: "200px", overflowY: "auto" }}>
                            <strong className="d-block mb-2">Hourly Breakdown:</strong>
                            {analytics.hourDistribution.map((count, hour) => {
                              const maxOpens = Math.max(...analytics.hourDistribution);
                              const isBestHour = maxOpens > 0 && count === maxOpens && hour === analytics.hourDistribution.indexOf(maxOpens);
                              return (
                                <div key={hour} className="d-flex justify-content-between mb-1">
                                  <span>
                                    {hour.toString().padStart(2, '0')}:00 - {(hour + 1).toString().padStart(2, '0')}:00
                                  </span>
                                  <Badge bg={isBestHour ? "primary" : count > 0 ? "secondary" : "light"}>
                                    {count}
                                  </Badge>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Unsubscribed Users Section */}
              <div className="mt-4 pt-3 border-top">
                <h6 className="mb-3">
                  <FaUsers className="me-2" />
                  Unsubscribed Users ({unsubscribedUsers.length})
                </h6>
                {loadingActivities ? (
                  <div className="text-center py-2">
                    <Spinner animation="border" size="sm" />
                    <p className="text-muted mt-2 mb-0 small">Loading unsubscribed users...</p>
                  </div>
                ) : unsubscribedUsers.length === 0 ? (
                  <p className="text-muted small mb-0">No unsubscribed users found</p>
                ) : (
                  <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                    {unsubscribedUsers.map((user, index) => (
                      <div key={user.id || index} className="mb-2 p-2 border rounded">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <Badge bg="danger" className="me-2">#{index + 1}</Badge>
                            <strong className="small">{user.email}</strong>
                          </div>
                          {user.timestamp && (
                            <small className="text-muted">
                              {user.timestamp?.toDate
                                ? user.timestamp.toDate().toLocaleString()
                                : user.timestamp?.toMillis
                                ? new Date(user.timestamp.toMillis()).toLocaleString()
                                : typeof user.timestamp === 'string'
                                ? new Date(user.timestamp).toLocaleString()
                                : "N/A"}
                            </small>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Activity Tracking Section */}
              <div className="mt-4 pt-3 border-top">
                <h6 className="mb-3">
                  <FaUsers className="me-2" />
                  Email Activity (Ranked)
                </h6>
                {loadingActivities || loadingOrders ? (
                  <div className="text-center py-2">
                    <Spinner animation="border" size="sm" />
                    <p className="text-muted mt-2 mb-0 small">Loading activities...</p>
                  </div>
                ) : rankedUsers.length === 0 ? (
                  <p className="text-muted small mb-0">No activity data available</p>
                ) : (
                  <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                    {rankedUsers.map((user, index) => (
                      <div key={index} className="mb-3 p-2 border rounded">
                        <div className="mb-2 d-flex justify-content-between align-items-center">
                          <div>
                            <Badge bg="warning" className="me-2">#{index + 1}</Badge>
                            <strong className="small">{user.email}</strong>
                          </div>
                          <div className="d-flex gap-1">
                            {user.orderValue > 0 && (
                              <Badge bg="success" title="Total Order Value">
                                ${user.orderValue.toFixed(2)}
                              </Badge>
                            )}
                            {user.orderCount > 0 && (
                              <Badge bg="info" title="Order Count">
                                {user.orderCount} orders
                              </Badge>
                            )}
                            {user.opens > 0 && (
                              <Badge bg="primary" title="Opens">
                                {user.opens} opens
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="d-flex flex-wrap gap-1">
                          {Object.entries(user.activities).map(([status, count]) => (
                            <Badge
                              key={status}
                              bg={
                                status === "sent"
                                  ? "success"
                                  : status === "delivered"
                                  ? "info"
                                  : status === "opened"
                                  ? "primary"
                                  : status === "clicked"
                                  ? "warning"
                                  : status === "bounced"
                                  ? "danger"
                                  : "secondary"
                              }
                              className="me-1"
                            >
                              {status}: {count}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
